import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Car, CheckCircle2, Clock, Sparkles } from "lucide-react";
import { SAR } from "../components/RiyalIcon";

const STATUS_AR = {
  NEW: "استلمنا طلبك — بانتظار البدء",
  PREPARING: "نجهز طلبك الآن",
  READY: "طلبك جاهز",
  CUSTOMER_ARRIVED: "تم إشعار الموظف — الطلب في طريقه للسيارة",
  OUT_FOR_DELIVERY: "طلبك خرج للتوصيل",
  COMPLETED: "تم استلام الطلب — شكراً لك",
  CANCELLED: "تم إلغاء الطلب",
};

export default function DemoOrderConfirmation() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [arriving, setArriving] = useState(false);

  const load = () => api.get(`/public/orders/${id}`).then(r => setOrder(r.data));
  useEffect(() => { load(); const t = setInterval(load, 4000); return () => clearInterval(t); }, [id]);

  if (!order) return <div className="p-8 text-center text-gray-500">جاري التحميل…</div>;

  const isCar = order.pickup_method === "CAR";
  const arrived = order.status === "CUSTOMER_ARRIVED";
  const done = order.status === "COMPLETED";

  const clickArrived = async () => {
    setArriving(true);
    try { await api.post(`/public/orders/${id}/arrived`); await load(); toast.success("تم إشعار الموظف"); }
    catch { toast.error("تعذر إرسال الإشعار"); }
    finally { setArriving(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] pb-10">
      <div className="max-w-md mx-auto px-5 py-6">
        <div className="mb-4 rounded-2xl bg-amber-100 text-amber-900 px-3 py-2 text-xs font-bold inline-flex items-center gap-1"><Sparkles size={14}/> طلب تجريبي</div>
        <div className="bg-white rounded-3xl card-shadow border border-orange-100 p-6 text-center rise-in">
          <div className="mx-auto w-16 h-16 rounded-full bg-green-100 grid place-items-center">
            <CheckCircle2 className="text-green-600" size={36} />
          </div>
          <h1 className="mt-3 text-2xl font-extrabold" data-testid="order-confirmed">تم استلام طلبك</h1>
          <div className="mt-1 text-gray-600 text-sm">طلب رقم <span className="font-extrabold text-gray-900 num-badge">#{order.order_number}</span></div>
          <div className="mt-4 inline-flex items-center gap-2 pill bg-orange-100 text-orange-800 px-3 py-1.5 text-sm font-bold">
            <Clock size={14}/> {STATUS_AR[order.status]}
          </div>
        </div>

        <div className="mt-5 bg-white rounded-2xl border border-orange-100 p-4">
          <div className="font-bold mb-3">تفاصيل الطلب</div>
          <div className="space-y-1.5 text-sm">
            {order.items.map((it, idx) => (
              <div key={idx} className="flex justify-between">
                <span>{it.quantity} × {it.name}</span>
                <span className="num-badge"><SAR>{it.line_total}</SAR></span>
              </div>
            ))}
          </div>
          <div className="border-t mt-3 pt-3 flex justify-between font-extrabold">
            <span>الإجمالي</span><span className="text-orange-600 num-badge"><SAR>{order.total}</SAR></span>
          </div>
        </div>

        {isCar && (
          <div className="mt-5 bg-white rounded-2xl border border-orange-100 p-4">
            <div className="font-bold mb-2 flex items-center gap-2"><Car size={18}/> بيانات السيارة</div>
            <div className="text-sm text-gray-700">{order.car_type} — {order.car_color} — <span className="num-badge">{order.car_plate}</span></div>
            {!arrived && !done && (
              <>
                <div className="text-xs text-gray-500 mt-3">إذا وصلت للمحل اضغط:</div>
                <button onClick={clickArrived} disabled={arriving}
                  className="mt-2 w-full pill bg-green-600 hover:bg-green-700 text-white font-extrabold text-lg py-4 tap-target disabled:opacity-60"
                  data-testid="arrived-btn">
                  🚗 وصلت
                </button>
              </>
            )}
            {arrived && (
              <div className="mt-3 text-green-700 font-bold text-sm">تم إشعار الموظف، انتظر لحظات وسيصل طلبك إلى السيارة.</div>
            )}
          </div>
        )}

        <div className="mt-6 flex justify-center gap-3">
          <Link to="/demo/customer" className="text-orange-600 font-bold text-sm">إعادة الطلب</Link>
          <span className="text-gray-300">·</span>
          <Link to="/demo/owner" className="text-gray-800 font-bold text-sm">شاهد اللوحة</Link>
        </div>
      </div>
    </div>
  );
}
